import React, { useMemo, useState } from 'react';
import { Layers, Palette, Pencil, Plus, Trash2 } from 'lucide-react';
import { Heading } from '../layout';
import { TabNavigation } from '../reusable';
import { FormDrawer } from '../forms';
import { useUIColors } from '../../hooks/useTheme';
import ProductionDataTable from './ProductionDataTable';
import ProductionStatusChip from './ProductionStatusChip';
import PaintFormulaDetailModal from './PaintFormulaDetailModal';
import useProductionStore from './useProductionStore';
import {
  emptyFormula,
  FORMULA_KIND_BASE,
  FORMULA_KIND_COLOR,
  FORMULA_KIND_LABELS,
  getActiveFormulaStatuses,
  getFormulaStatusColor,
  formatDateEs,
  formatQty,
  formulaKindLabel,
  nextFormulaCode,
  normalizeFormulaKind,
  PAINT_PRODUCT_OPTIONS,
  productLabel,
} from './productionDomain';
import { activeMaterials } from './rawMaterialsDomain';

const cell = (children, align = 'left') => (
  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: align }}>{children}</td>
);

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 36,
  padding: '0 12px',
  borderRadius: 8,
  border: 'none',
  background: '#16a34a',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
};

const BomEditor = ({ value = [], onChange, materials, bases = [], showBases = false, colors, baseInputStyle }) => {
  const lines = Array.isArray(value) && value.length ? value : [emptyFormula().lines[0]];

  const updateLine = (index, patch) => {
    onChange(lines.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  const fillComponent = (index, code) => {
    const base = bases.find((row) => row.code === code);
    if (base) {
      updateLine(index, {
        item_code: code,
        item_name: base.product_name || base.code,
        kind: 'Base',
        unit: base.yield_unit || 'L',
      });
      return;
    }
    const material = materials.find((row) => row.code === code);
    updateLine(index, {
      item_code: code,
      item_name: material?.name || '',
      kind: 'Materias primas',
      unit: material?.unit || 'kg',
    });
  };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {lines.map((line, index) => (
        <div
          key={`bom-${index}`}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.4fr) 88px 70px 70px 32px',
            gap: 6,
            alignItems: 'end',
          }}
        >
          <label style={{ fontSize: 11, color: colors.text }}>
            Componente
            <select
              value={line.item_code || ''}
              onChange={(event) => fillComponent(index, event.target.value)}
              style={baseInputStyle}
            >
              <option value="">Seleccionar…</option>
              {showBases && bases.length ? (
                <optgroup label="Bases activas">
                  {bases.map((base) => (
                    <option key={base.code} value={base.code}>
                      {base.code} · {base.product_name}
                    </option>
                  ))}
                </optgroup>
              ) : null}
              <optgroup label="Materias primas">
                {materials.map((material) => (
                  <option key={material.code} value={material.code}>
                    {material.code}
                    {material.procurement_code ? ` · ${material.procurement_code}` : ''} · {material.name}
                  </option>
                ))}
              </optgroup>
            </select>
          </label>
          <label style={{ fontSize: 11, color: colors.text }}>
            Cantidad
            <input
              type="number"
              min="0"
              step="0.01"
              value={line.quantity}
              onChange={(event) => updateLine(index, { quantity: event.target.value })}
              style={baseInputStyle}
            />
          </label>
          <label style={{ fontSize: 11, color: colors.text }}>
            Unidad
            <input value={line.unit || ''} readOnly style={{ ...baseInputStyle, opacity: 0.8 }} />
          </label>
          <label style={{ fontSize: 11, color: colors.text }}>
            Merma %
            <input
              type="number"
              min="0"
              step="0.1"
              value={line.scrap_pct}
              onChange={(event) => updateLine(index, { scrap_pct: event.target.value })}
              style={baseInputStyle}
            />
          </label>
          <button
            type="button"
            onClick={() => onChange(lines.filter((_, i) => i !== index))}
            disabled={lines.length <= 1}
            style={{
              height: 36,
              border: 'none',
              background: 'transparent',
              color: colors.danger || '#dc2626',
              cursor: lines.length <= 1 ? 'not-allowed' : 'pointer',
            }}
            aria-label="Quitar componente"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...lines, { ...emptyFormula().lines[0] }])}
        style={{
          ...primaryBtn,
          height: 32,
          background: 'transparent',
          color: colors.text,
          border: `1px solid ${colors.inputBorder}`,
          justifySelf: 'start',
        }}
      >
        <Plus size={14} /> Agregar componente
      </button>
    </div>
  );
};

const FormulaKindPanel = ({
  kind,
  formulas,
  statusFilter,
  onStatusFilter,
  onCreate,
  onEdit,
  onOpen,
}) => {
  const ui = useUIColors();
  const isColor = kind === FORMULA_KIND_COLOR;
  const rows = useMemo(
    () =>
      formulas
        .filter((row) => normalizeFormulaKind(row) === kind)
        .filter((row) => statusFilter === 'all' || row.status === statusFilter)
        .map((row) => ({
          ...row,
          components: Array.isArray(row.lines) ? row.lines.length : 0,
        })),
    [formulas, kind, statusFilter]
  );

  const columns = isColor
    ? [
        { key: 'Código', sortKey: 'code' },
        { key: 'Producto', sortKey: 'product_name' },
        { key: 'Color', sortKey: 'color_code' },
        { key: 'Base', sortKey: 'base_formula_code' },
        { key: 'Versión', sortKey: 'version', align: 'right' },
        { key: 'Rendimiento', sortKey: 'yield_qty', align: 'right' },
        { key: 'Estado', sortKey: 'status', align: 'center' },
        { key: 'Actualizada', sortKey: 'updated_at' },
        { key: '', sortKey: null, align: 'right' },
      ]
    : [
        { key: 'Código', sortKey: 'code' },
        { key: 'Producto / base', sortKey: 'product_name' },
        { key: 'Versión', sortKey: 'version', align: 'right' },
        { key: 'Rendimiento', sortKey: 'yield_qty', align: 'right' },
        { key: 'Componentes', sortKey: 'components', align: 'right' },
        { key: 'Estado', sortKey: 'status', align: 'center' },
        { key: 'Actualizada', sortKey: 'updated_at' },
        { key: '', sortKey: null, align: 'right' },
      ];

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <select
          value={statusFilter}
          onChange={(event) => onStatusFilter(event.target.value)}
          style={{
            height: 36,
            borderRadius: 8,
            border: `1px solid ${ui.inputBorder}`,
            background: ui.inputBg,
            color: ui.text,
            padding: '0 10px',
            fontSize: 13,
          }}
        >
          <option value="all">Todos los estados</option>
          {getActiveFormulaStatuses().map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>
      <ProductionDataTable
        title={isColor ? 'Fórmulas de colores' : 'Fórmulas de bases'}
        subtitle={`${rows.length} fórmulas`}
        rows={rows}
        columns={columns}
        defaultSortKey="code"
        searchPlaceholder={
          isColor ? 'Buscar código, color, RAL, base…' : 'Buscar código, producto, versión…'
        }
        onRowClick={onOpen}
        action={
          <button type="button" onClick={() => onCreate(kind)} style={primaryBtn}>
            <Plus size={14} /> {isColor ? 'Nuevo color' : 'Nueva base'}
          </button>
        }
        renderCells={(row, colors) => (
          <>
            {cell(<strong>{row.code}</strong>)}
            {cell(`${row.product_code} · ${row.product_name}`)}
            {isColor ? cell(row.color_code ? `${row.color_code} · ${row.color_name || ''}` : '—') : null}
            {isColor ? cell(row.base_formula_code || '—') : null}
            {cell(`v${row.version}`, 'right')}
            {cell(formatQty(row.yield_qty, row.yield_unit), 'right')}
            {isColor ? null : cell(row.components, 'right')}
            {cell(
              <ProductionStatusChip status={row.status} color={getFormulaStatusColor(row.status)} />,
              'center'
            )}
            {cell(formatDateEs(row.updated_at))}
            {cell(
              <button
                type="button"
                title="Editar"
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit(row);
                }}
                style={{
                  height: 28,
                  width: 28,
                  borderRadius: 6,
                  border: `1px solid ${colors.border}`,
                  background: 'transparent',
                  color: colors.text,
                  cursor: 'pointer',
                }}
              >
                <Pencil size={13} />
              </button>,
              'right'
            )}
          </>
        )}
      />
    </div>
  );
};

const PaintFormulasWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const { formulas, materials, persistFormulas } = useProductionStore();
  const catalog = useMemo(() => activeMaterials(materials), [materials]);
  const activeBases = useMemo(
    () =>
      formulas.filter(
        (row) => normalizeFormulaKind(row) === FORMULA_KIND_BASE && row.status === 'Activa'
      ),
    [formulas]
  );
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(emptyFormula());
  const [formError, setFormError] = useState('');
  const [detail, setDetail] = useState(null);

  const formKind = normalizeFormulaKind(formData);
  const isColorForm = formKind === FORMULA_KIND_COLOR;

  const openCreate = (kind) => {
    setFormData(emptyFormula(kind));
    setIsEditing(false);
    setFormError('');
    setShowForm(true);
    setDetail(null);
  };

  const openEdit = (row) => {
    setFormData({
      ...emptyFormula(normalizeFormulaKind(row)),
      ...row,
      base_formula_id: row.base_formula_id ? String(row.base_formula_id) : '',
      lines: Array.isArray(row.lines) && row.lines.length ? row.lines : emptyFormula().lines,
    });
    setIsEditing(true);
    setFormError('');
    setShowForm(true);
    setDetail(null);
  };

  const handleChange = (key, value) => {
    setFormData((current) => {
      const kind = normalizeFormulaKind(current);
      const next = { ...current, [key]: value };
      if (key === 'product_code') {
        const option = PAINT_PRODUCT_OPTIONS.find((item) => item.value === value);
        next.product_name = option ? option.label.replace(/^[A-Z0-9]+\s·\s/, '') : '';
        if (!isEditing) {
          const generated = nextFormulaCode(formulas, value, kind);
          next.code = generated.code;
          next.version = generated.version;
        }
      }
      if (key === 'base_formula_id') {
        const base = activeBases.find((row) => String(row.id) === String(value));
        next.base_formula_code = base?.code || '';
        if (base) {
          const rest = (current.lines || []).filter((line) => line.kind !== 'Base');
          next.lines = [
            {
              item_code: base.code,
              item_name: base.product_name,
              kind: 'Base',
              quantity: current.yield_qty || 100,
              unit: base.yield_unit || 'L',
              scrap_pct: 1,
            },
            ...(rest.length ? rest : [emptyFormula().lines[0]]),
          ];
        }
      }
      return next;
    });
  };

  const persist = (status) => {
    const productCode = String(formData.product_code || '').trim();
    const lines = (formData.lines || []).filter((line) => line.item_code && Number(line.quantity) > 0);
    if (!productCode) {
      setFormError('Selecciona el producto destino.');
      return false;
    }
    if (isColorForm && !formData.base_formula_id) {
      setFormError('Selecciona la fórmula de base sobre la que se tintea el color.');
      return false;
    }
    if (isColorForm && !String(formData.color_code || formData.color_name || '').trim()) {
      setFormError('Indica el código o el nombre del color.');
      return false;
    }
    if (!lines.length) {
      setFormError('Agrega al menos un componente con cantidad.');
      return false;
    }
    const generated = nextFormulaCode(
      formulas.filter((row) => row.id !== formData.id),
      productCode,
      formKind
    );
    const code = isEditing && formData.code ? formData.code : generated.code;
    const version = isEditing && formData.version ? formData.version : generated.version;
    const base = activeBases.find((row) => String(row.id) === String(formData.base_formula_id));
    const payload = {
      ...formData,
      formula_kind: formKind,
      code,
      version,
      product_code: productCode,
      product_name: formData.product_name || productLabel(productCode),
      color_code: isColorForm ? String(formData.color_code || '').trim() : '',
      color_name: isColorForm ? String(formData.color_name || '').trim() : '',
      base_formula_id: isColorForm ? Number(formData.base_formula_id) || null : null,
      base_formula_code: isColorForm ? base?.code || formData.base_formula_code || '' : '',
      yield_qty: Number(formData.yield_qty) || 0,
      status,
      lines,
      updated_at: new Date().toISOString().slice(0, 10),
    };

    persistFormulas((current) => {
      let next = current;
      if (status === 'Activa') {
        next = next.map((row) => {
          const sameFamily =
            normalizeFormulaKind(row) === formKind &&
            row.product_code === productCode &&
            row.status === 'Activa' &&
            row.id !== formData.id;
          const sameColor =
            !isColorForm ||
            String(row.color_code || '').trim().toLowerCase() ===
              String(payload.color_code).toLowerCase();
          return sameFamily && sameColor ? { ...row, status: 'Obsoleta' } : row;
        });
      }
      if (isEditing && formData.id) {
        return next.map((row) => (row.id === formData.id ? { ...row, ...payload } : row));
      }
      const id = next.reduce((max, row) => Math.max(max, Number(row.id) || 0), 0) + 1;
      return [...next, { ...payload, id }];
    });
    setShowForm(false);
    return true;
  };

  const sections = [
    {
      key: 'header',
      title: isColorForm ? 'Producto, color y rendimiento' : 'Producto y rendimiento',
      columns: 2,
      fields: [
        {
          key: 'product_code',
          label: 'Producto',
          type: 'select',
          options: [
            { value: '', label: 'Seleccionar producto' },
            ...PAINT_PRODUCT_OPTIONS,
          ],
        },
        { key: 'code', label: 'Código', readOnly: true },
        ...(isColorForm
          ? [
              { key: 'color_code', label: 'Código de color', placeholder: 'RAL 7035 / SW 7008' },
              { key: 'color_name', label: 'Nombre del color' },
              {
                key: 'base_formula_id',
                label: 'Fórmula de base',
                type: 'select',
                options: [
                  { value: '', label: 'Seleccionar base activa' },
                  ...activeBases.map((base) => ({
                    value: String(base.id),
                    label: `${base.code} · ${base.product_name}`,
                  })),
                ],
              },
            ]
          : []),
        { key: 'yield_qty', label: 'Rendimiento', type: 'number', step: '0.1' },
        {
          key: 'yield_unit',
          label: 'Unidad',
          type: 'select',
          options: [
            { value: 'L', label: 'L' },
            { value: 'kg', label: 'kg' },
          ],
        },
        { key: 'version', label: 'Versión', type: 'number' },
        { key: 'notes', label: 'Notas', type: 'textarea', fullWidth: true, rows: 2 },
      ],
    },
    {
      key: 'bom',
      title: isColorForm
        ? 'Tintado (base + pigmentos / colorantes)'
        : 'Componentes de la base (por unidad de rendimiento)',
      columns: 1,
      fields: [
        {
          key: 'lines',
          type: 'custom',
          fullWidth: true,
          render: ({ value, onChange, colors, baseInputStyle }) => (
            <BomEditor
              value={value}
              onChange={onChange}
              materials={catalog}
              bases={activeBases}
              showBases={isColorForm}
              colors={colors}
              baseInputStyle={baseInputStyle}
            />
          ),
        },
      ],
    },
  ];

  const tabs = [
    {
      key: FORMULA_KIND_BASE,
      label: FORMULA_KIND_LABELS.base,
      icon: <Layers size={16} />,
      content: (
        <FormulaKindPanel
          kind={FORMULA_KIND_BASE}
          formulas={formulas}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          onCreate={openCreate}
          onEdit={openEdit}
          onOpen={setDetail}
        />
      ),
    },
    {
      key: FORMULA_KIND_COLOR,
      label: FORMULA_KIND_LABELS.color,
      icon: <Palette size={16} />,
      content: (
        <FormulaKindPanel
          kind={FORMULA_KIND_COLOR}
          formulas={formulas}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          onCreate={openCreate}
          onEdit={openEdit}
          onOpen={setDetail}
        />
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: 6 }}>
          Fórmulas
        </Heading>
        <p style={{ margin: 0, maxWidth: 720, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>
          Las bases definen el vehículo (resina, solvente, carga). Los colores tintean una base activa
          con pigmentos para un RAL u otro código de color.
        </p>
      </div>

      <TabNavigation tabs={tabs} defaultTab={FORMULA_KIND_BASE} isDarkMode={isDarkMode} />

      <FormDrawer
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSave={() => persist(formData.status === 'Activa' ? 'Activa' : 'Borrador')}
        isDarkMode={isDarkMode}
        title={
          isEditing
            ? `Editar fórmula de ${formulaKindLabel(formKind).toLowerCase()}`
            : `Nueva fórmula de ${formulaKindLabel(formKind).toLowerCase()}`
        }
        subtitle={formData.code || (isColorForm ? 'Tintado sobre base' : 'Vehículo de producción')}
        sections={sections}
        formData={formData}
        onChange={handleChange}
        formError={formError}
        width={720}
        footerActions={[
          { key: 'cancel', label: 'Cancelar', onClick: () => setShowForm(false) },
          {
            key: 'draft',
            label: 'Guardar borrador',
            onClick: () => persist('Borrador'),
          },
          {
            key: 'active',
            label: isColorForm ? 'Activar color' : 'Activar base',
            variant: 'success',
            onClick: () => persist('Activa'),
          },
        ]}
      />

      <PaintFormulaDetailModal
        isOpen={Boolean(detail)}
        onClose={() => setDetail(null)}
        formula={detail}
        isDarkMode={isDarkMode}
        ui={ui}
        onEdit={openEdit}
      />
    </div>
  );
};

export default PaintFormulasWorkspace;
